/**
 * Employees service — queries and mutations for roster, credentials, profiles.
 */
import { createClient } from '@/lib/supabase/client'
import type { Database } from '#modules/database'
import type { WithQuals } from '@/lib/qualificationUtils'
import { QUAL_FIELDS } from '#modules/qualifications'

/** Base employee type returned by queryActiveEmployees and used throughout the app. */
export type Employee = { id: string; name: string } & WithQuals

// PostgREST select for embedding an employee's qualifications.
export const EMP_QUALS = `employee_qualifications(qualifications(${QUAL_FIELDS}))`

// -- Queries ------------------------------------------------------------------

/** Get all active employees with qualifications */
export async function queryActiveEmployees(): Promise<{ data: Employee[] | null; error: any }> {
  const result = await createClient()
    .from('employees')
    .select(`id, name, ${EMP_QUALS}`)
    .eq('status', 'Active')
    .order('name')
  return { data: result.data as unknown as Employee[] | null, error: result.error }
}

/** Search active employees by partial name match. Used for autocomplete/search inputs. */
export async function searchEmployees(nameQuery: string, limit: number): Promise<{ data: Employee[] | null; error: any }> {
  const result = await createClient()
    .from('employees')
    .select(`id, name, ${EMP_QUALS}`)
    .eq('status', 'Active')
    .ilike('name', `%${nameQuery}%`)
    .order('name')
    .limit(limit)
  return { data: result.data as unknown as Employee[] | null, error: result.error }
}

/** Predicate fetch: single employee by ID from the PII view */
export function queryEmployeePII(id: string) {
  return createClient()
    .from('employees_pii')
    .select(`*, ${EMP_QUALS}`)
    .eq('id', id)
    .single()
}

/** Predicate fetch: single employee by ID from the public view */
export function queryEmployee(id: string) {
  return createClient()
    .from('employees')
    .select(`*, ${EMP_QUALS}`)
    .eq('id', id)
    .single()
}

/** Get employee credentials */
export function queryCredentials(employeeId: string) {
  return createClient()
    .from('employee_credentials')
    .select('*')
    .eq('employee_id', employeeId)
    .order('cert_type')
}

/** Current user's employee record with qualifications. */
export async function queryCurrentEmployee() {
  const { data, error } = await createClient().rpc('get_current_employee')
  return { data: (data ?? null) as Employee | null, error }
}

// -- Mutations ----------------------------------------------------------------

/** Update employee fields */
export function updateEmployee(id: string, data: Database['public']['Views']['employees_pii']['Update']) {
  return createClient()
    .from('employees_pii')
    .update(data)
    .eq('id', id)
}
